/**
 * React hooks for mockExam feature.
 * Provides typed access to mockExam data via TanStack Query.
 */

import {
  useMockExamListQuery,
  useMockExamDetailQuery,
  useCreateMockExamSessionMutation,
  useSubmitMockExamMutation,
  useMockExamResultQuery,
} from "@/infrastructure/adapters/mock-exam/mock-exam.query";

/** Hook: Fetch the list of active mock exams */
export const useMockExamList = (options?: { enabled?: boolean }) => {
  const listQuery = useMockExamListQuery(options);

  return {
    mockExams: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    error: listQuery.error,
  };
};

/** Hook: Fetch one mockExam overview (metadata + attempts) by id */
export const useMockExamDetail = (mockExamId: number, options?: { enabled?: boolean }) => {
  const detailQuery = useMockExamDetailQuery(mockExamId, options);

  return {
    mockExamId: detailQuery.data?.mockExamId ?? mockExamId,
    name: detailQuery.data?.name ?? "",
    metadata: detailQuery.data?.metadata ?? {
      totalQuestions: 0,
      timeLimitMinutes: 0,
      passingScorePercentage: 0,
      passingQuestionCount: 0,
      totalScore: 0,
      passingScore: 0,
    },
    attempts: detailQuery.data?.attempts ?? [],
    isLoading: detailQuery.isLoading,
  };
};

/** Hook: Create a new mockExam session for the specified exam */
export const useCreateMockExamSession = () => {
  const mutation = useCreateMockExamSessionMutation();

  return {
    session: mutation.data,
    createSession: mutation.mutateAsync,
    isCreatingSession: mutation.isPending,
  };
};

/** Hook: Submit a mockExam session */
export const useSubmitMockExam = () => {
  const mutation = useSubmitMockExamMutation();

  return {
    submitMockExam: mutation.mutateAsync,
    isSubmitting: mutation.isPending,
  };
};

/** Hook: Fetch mockExam result for a completed attempt if the attemptId is a valid number and more than 0 */
export const useMockExamResult = (attemptId: number, options?: { enabled?: boolean }) => {
  const query = useMockExamResultQuery(attemptId, options);
  return {
    result: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
